/**
 * Scroll depth tracker for Mixpanel (fhs-scroll-depth-reached).
 * Tracks when users reach scroll depth thresholds: 25%, 50%, 75%, 100%.
 * Each threshold fires once per page view. Uses throttled scroll handler.
 *
 * Expects window.fhsAnalytics and window.fhsAnalytics.context from analytics.js.
 * Expects window.fhsAnalytics.shouldTrack from html-body-tracking.twig (bridged from PHP should_track).
 */

import { getDeviceType } from './getDeviceType.js';

const SCROLL_EVENT = 'fhs-scroll-depth-reached';
const THRESHOLDS = [25, 50, 75, 100];
const THROTTLE_MS = 150;

/**
 * Calculate scroll percentage.
 * Formula: (scrollTop + viewportHeight) / documentHeight
 * Handles dynamic content by recalculating on each check.
 * @returns {number} 0–100
 */
function getScrollDepth() {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return 0;
  }
  const doc = document.documentElement;
  const scrollTop = window.scrollY ?? doc.scrollTop ?? 0;
  const viewportHeight = window.innerHeight ?? doc.clientHeight ?? 0;
  const docHeight = Math.max(
    doc.scrollHeight ?? 0,
    doc.offsetHeight ?? 0,
    (doc.clientHeight ?? 0) + scrollTop,
    document.body?.scrollHeight ?? 0,
    document.body?.offsetHeight ?? 0
  );
  if (docHeight <= 0) return 0;
  const depth = ((scrollTop + viewportHeight) / docHeight) * 100;
  return Math.min(100, Math.max(0, depth));
}

/**
 * Build scroll depth event properties.
 * track() merges context (page_type, template_name, fh_ct, theme, etc.).
 * @param {number} bucket - Threshold reached (25, 50, 75, 100)
 * @param {number} [timeToDepthMs] - Time from page load to threshold
 */
function getScrollDepthProperties(bucket, timeToDepthMs) {
  const props = {
    scroll_depth: bucket,
    device_type: getDeviceType(),
  };
  if (typeof timeToDepthMs === 'number' && timeToDepthMs >= 0) {
    props.time_to_depth_ms = Math.round(timeToDepthMs);
  }
  return props;
}

/**
 * Throttle a function.
 * @param {Function} fn
 * @param {number} ms
 * @returns {Function}
 */
function throttle(fn, ms) {
  let last = 0;
  let timeout = null;
  return function throttled(...args) {
    const now = Date.now();
    const elapsed = now - last;
    if (elapsed >= ms) {
      last = now;
      fn.apply(this, args);
    } else if (!timeout) {
      timeout = setTimeout(
        () => {
          last = Date.now();
          timeout = null;
          fn.apply(this, args);
        },
        ms - elapsed
      );
    }
  };
}

/**
 * Initialize scroll depth tracking.
 */
function init() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  if (!window.fhsAnalytics?.track) return;
  if (window.fhsAnalytics.shouldTrack === false) return;

  const reached = new Set();
  const pageLoadTime = performance.now?.() ?? Date.now();

  const throttledCheck = throttle(checkAndFire, THROTTLE_MS);

  function cleanup() {
    window.removeEventListener('scroll', throttledCheck);
    window.removeEventListener('resize', throttledCheck);
  }

  function checkAndFire() {
    const depth = getScrollDepth();
    for (const bucket of THRESHOLDS) {
      if (depth >= bucket && !reached.has(bucket)) {
        reached.add(bucket);
        const timeToDepthMs = (performance.now?.() ?? Date.now()) - pageLoadTime;
        const props = getScrollDepthProperties(bucket, timeToDepthMs);
        window.fhsAnalytics.track(SCROLL_EVENT, props);
      }
    }
    if (reached.size === THRESHOLDS.length) {
      cleanup();
    }
  }

  window.addEventListener('scroll', throttledCheck, { passive: true });

  // Check immediately (user may have scrolled before listener attached, or page is short)
  if (document.readyState === 'complete') {
    checkAndFire();
  } else {
    window.addEventListener('load', checkAndFire);
  }

  // Recalculate on resize (viewport height changes affect depth)
  window.addEventListener('resize', throttledCheck);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
